// components/dashboard/ProfileTab.jsx
// Desktop port of the mobile Profile screen:
//   • Left rail: avatar + identity + "View as a boss" + section nav
//   • Right scroll: job preferences, prompts, social links, work history,
//     education, achievements, projects, resume
// All sections share the Home palette (whites + zinc + ink).
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import {
  Camera, Eye, BarChart3, Pencil, Plus, X, Trash2,
  ExternalLink, PenTool, Link2, Quote, MapPin, Clock, DollarSign,
  Briefcase, GraduationCap, Trophy, FolderGit2, FileText, Mail, Phone,
  Check, Download, Sparkles, Hash, Loader2, ArrowRight, TrendingUp,
  ChevronDown,
} from "lucide-react"
import githubSvg   from "../../assets/github.svg"
import linkedinPng from "../../assets/linkedin.png"
import leetcodePng from "../../assets/leetcode.png"
import mediumPng   from "../../assets/medium.png"
import dribbblePng from "../../assets/dribbble.png"
import youtubePng  from "../../assets/youtube.png"
import toast from "react-hot-toast"
import {
  doc as fsDoc, setDoc, updateDoc, deleteField, deleteDoc, collection, getDoc,
  onSnapshot,
} from "firebase/firestore"
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "../../firebaseConfig"
import AddResumeModal from "../AddResumeModal"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

if (pdfjs?.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"
}

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const API_BASE = "https://jobscanner-e1dd4.ue.r.appspot.com"

// ── Palette (matches Home / WildTab / InboxTab) ──────────────────────────
const P = {
  bg:         "#F7F7F8",
  surface:    "#FFFFFF",
  surfaceAlt: "#FAFAFA",
  border:     "#E4E4E7",
  divider:    "#EEEEEE",
  ink:        "#18181b",
  ink2:       "#27272a",
  inkMuted:   "#52525b",
  inkDim:     "#71717a",
  inkFaint:   "#a1a1aa",
  pillBg:     "#F4F4F5",
  danger:     "#DC2626",
  success:    "#16A34A",
  linkedin:   "#0A66C2",
  github:     "#24292F",
  leetcode:   "#F89F1B",
  codeforces: "#1F8ACB",
  medium:     "#000000",
  dribbble:   "#EA4C89",
  youtube:    "#FF0000",
  portfolio:  "#6366F1",
}

// ── Constants ────────────────────────────────────────────────────────────
const PROMPT_SUGGESTIONS = [
  "The best product I've worked on…",
  "What I'm currently working on",
  "What I've built with AI",
  "I'm the person in every team who…",
  "What's hard for others but easy for me…",
  "My hot take about working with AI is…",
  "My biggest achievement in college was…",
  "My proudest non-work achievement",
  "The last shipped feature I'm proudest of…",
  "The role I'd take a 20% pay cut for",
  "The hardest problem I've personally solved…",
]
const NOTICE_PERIODS = [
  "Immediate Joiner", "Upto 15 days", "Upto 30 days",
  "Upto 45 days", "Upto 60 days", "Upto 90 days",
]
const CITY_KEYS  = ["bengaluru", "mumbai", "delhi_ncr", "hyderabad", "pune", "chennai", "anywhere"]
const CITY_NAMES = ["Bengaluru", "Mumbai", "Delhi NCR", "Hyderabad", "Pune", "Chennai", "Anywhere in India"]

const SOCIAL_TYPES = [
  { k: "linkedin",   label: "LinkedIn",            prefix: "linkedin.com/in/",      host: "linkedin.com",   color: P.linkedin },
  { k: "github",     label: "GitHub",              prefix: "github.com/",           host: "github.com",     color: P.github },
  { k: "leetcode",   label: "LeetCode",            prefix: "leetcode.com/u/",       host: "leetcode.com",   color: P.leetcode },
  { k: "codeforces", label: "Codeforces",          prefix: "codeforces.com/profile/", host: "codeforces.com", color: P.codeforces },
  { k: "portfolio",  label: "Personal portfolio",  prefix: "https://",              host: null,             color: P.portfolio },
  { k: "medium",     label: "Medium",              prefix: "medium.com/@",          host: "medium.com",     color: P.medium },
  { k: "dribbble",   label: "Dribbble",            prefix: "dribbble.com/",         host: "dribbble.com",   color: P.dribbble },
  { k: "youtube",    label: "YouTube",             prefix: "youtube.com/@",         host: "youtube.com",    color: P.youtube },
]
const SOCIAL_MAP = Object.fromEntries(SOCIAL_TYPES.map(t => [t.k, t]))

// ── Helpers ──────────────────────────────────────────────────────────────
const toInt = (v) => {
  if (v == null) return 0
  if (typeof v === "number") return v
  const n = parseInt(String(v).replace(/,/g, ""), 10)
  return Number.isFinite(n) ? n : 0
}

function initialsOf(name = "") {
  return (name || "")
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "").join("") || "?"
}

function prettyCity(v = "") {
  if (!v) return ""
  if (v === "Anywhere in India") return v
  const idx = CITY_KEYS.indexOf(v)
  if (idx >= 0) return CITY_NAMES[idx]
  return v[0].toUpperCase() + v.slice(1)
}

function shortLevel(level = "") {
  const map = { SENIOR: "Senior", MID: "Mid-level", JUNIOR: "Junior", LEAD: "Lead", PRINCIPAL: "Principal" }
  return map[String(level).toUpperCase()] || level
}

function lakhLabel(rupees) {
  const n = toInt(rupees)
  if (n <= 0) return ""
  const lakh = n / 100000
  const rounded = Math.round(lakh * 10) / 10
  return `₹${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} LPA`
}

function indianFormatInput(raw) {
  const digits = String(raw).replace(/\D+/g, "")
  if (!digits) return ""
  const n = parseInt(digits, 10)
  if (!Number.isFinite(n)) return ""
  return n.toLocaleString("en-IN")
}

function normalizeSocialUrl(type, input) {
  const t = SOCIAL_MAP[type]
  if (!t) return null
  const value = String(input || "").trim()
  if (!value) return null
  const lower = value.toLowerCase()
  if (t.host && (lower === t.host || lower === `www.${t.host}`)) return null
  const strippedHandle = (type === "medium" || type === "youtube") && value.startsWith("@")
    ? value.slice(1) : value
  let raw
  if (value.startsWith("http://") || value.startsWith("https://")) {
    raw = value
  } else if (value.includes(".") && value.includes("/")) {
    raw = `https://${value}`
  } else if (type === "portfolio") {
    raw = `https://${value}`
  } else {
    raw = `https://${t.prefix}${strippedHandle}`
  }
  let u
  try { u = new URL(raw) } catch { return null }
  if (!["http:", "https:"].includes(u.protocol)) return null
  if (!u.host) return null
  if (t.host) {
    const inputHost = u.host.toLowerCase()
    if (inputHost !== t.host && inputHost !== `www.${t.host}`) return null
  } else {
    // Portfolio must look like a real domain
    const okDomain = /^(?=.{4,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/
      .test(u.host)
    if (!okDomain) return null
    u.hash = ""
    u.protocol = "https:"
    return u.toString()
  }
  const parts = u.pathname.split("/").filter(Boolean)
  if (parts.length === 0) return null
  u.pathname = "/" + parts.join("/")
  u.hash = ""
  u.search = ""
  u.protocol = "https:"
  return u.toString()
}

function fmt(month = "", year = "") {
  return [month, year].filter(Boolean).join(" ")
}

// Logo endpoint — mirrors mobile usage
function companyLogoUrl(company) {
  if (!company) return ""
  return `${API_BASE}/users/logo?company=${encodeURIComponent(company)}`
}

// ── Reusable: section card ───────────────────────────────────────────────
function SectionCard({ id, title, eyebrow, subtitle, icon, count, action, children }) {
  return (
    <section id={id} data-pf-section={id} style={{
      background: P.surface, borderRadius: 16,
      padding: "22px 24px 20px", marginBottom: 14, fontFamily: FF,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}>
      <header style={{
        display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18,
      }}>
        {icon && (
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            background: P.pillBg, color: P.ink,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, marginTop: 1,
          }}>
            {icon}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {eyebrow && (
            <div style={{
              fontSize: 10, fontWeight: 700, color: P.inkFaint,
              letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 4,
            }}>
              {eyebrow}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <h3 style={{
              margin: 0, fontSize: 17, fontWeight: 800, color: P.ink,
              letterSpacing: "-0.025em",
            }}>
              {title}
            </h3>
            {typeof count === "number" && count > 0 && (
              <span style={{
                padding: "2px 9px", borderRadius: 999, background: P.pillBg,
                color: P.inkDim, fontSize: 11, fontWeight: 700,
                letterSpacing: "0.02em",
              }}>
                {count}
              </span>
            )}
          </div>
          {subtitle && (
            <div style={{
              marginTop: 4, fontSize: 12.5, color: P.inkMuted, lineHeight: 1.5,
            }}>
              {subtitle}
            </div>
          )}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </header>
      {children}
    </section>
  )
}

function GhostButton({ children, onClick, danger, icon, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 11px", borderRadius: 8, border: "none",
      background: P.pillBg, color: danger ? P.danger : P.ink,
      fontSize: 12, fontWeight: 600, fontFamily: FF,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      transition: "background 0.12s",
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = "#EBEBEE" }}
    onMouseLeave={e => { e.currentTarget.style.background = P.pillBg }}
    >
      {icon}
      {children}
    </button>
  )
}

function PrimaryButton({ children, onClick, disabled, type = "button" }) {
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: 6, padding: "9px 16px", borderRadius: 10, border: "none",
        background: disabled ? P.pillBg : P.ink,
        color: disabled ? P.inkFaint : "#fff",
        fontSize: 13, fontWeight: 700, fontFamily: FF,
        cursor: disabled ? "not-allowed" : "pointer",
        letterSpacing: "-0.01em",
      }}>
      {children}
    </button>
  )
}

// ── Modal shell ──────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 520 }) {
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "" }
  }, [])
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose])
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(15,23,42,0.42)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: FF,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth, maxHeight: "90vh",
        background: P.surface, borderRadius: 18,
        boxShadow: "0 24px 48px rgba(15,23,42,0.18)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 20px",
        }}>
          <h3 style={{
            margin: 0, flex: 1, fontSize: 15, fontWeight: 700, color: P.ink,
            letterSpacing: "-0.015em",
          }}>
            {title}
          </h3>
          <button onClick={onClose} aria-label="Close" style={{
            width: 30, height: 30, borderRadius: 8, border: "none",
            background: P.pillBg, color: P.ink, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <X size={15} />
          </button>
        </div>
        <div style={{ padding: "4px 20px 20px", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  )
}

// ── Hero / left rail ─────────────────────────────────────────────────────
function HeroCard({ userData, userId, previewMode, onTogglePreview }) {
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)
  const name = userData?.name || "—"
  const email = userData?.email || ""
  const phone = userData?.phone || userData?.additionalInfo?.phone || ""
  const avatar = userData?.profileUrl || ""

  const pickFile = () => fileRef.current?.click()
  const onFile = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ""
    if (!f || !userId) return
    if (!f.type.startsWith("image/")) { toast.error("choose an image file"); return }
    if (f.size > 5 * 1024 * 1024) { toast.error("image too large (5MB max)"); return }
    setUploading(true)
    try {
      const ext = f.name.split(".").pop()?.toLowerCase() || "jpg"
      const ref = storageRef(storage, `profileImages/${userId}/profile.${ext}`)
      await uploadBytes(ref, f)
      const url = await getDownloadURL(ref)
      await setDoc(fsDoc(db, "users", userId), { profileUrl: url }, { merge: true })
      toast.success("photo updated")
    } catch (err) {
      console.error("Photo upload failed:", err)
      toast.error("couldn't upload photo. try again.")
    } finally { setUploading(false) }
  }

  return (
    <aside style={{
      background: P.surface, borderRadius: 16,
      padding: 20, display: "flex", flexDirection: "column", gap: 16,
      fontFamily: FF, position: "sticky", top: 0,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}>
      {/* Avatar */}
      <div style={{
        position: "relative", width: "100%", aspectRatio: "1 / 1",
        borderRadius: 14, overflow: "hidden", background: P.pillBg,
      }}>
        {avatar ? (
          <img src={avatar} alt=""
               style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #FFFFFF, #F4F4F5)",
            color: P.inkMuted, fontSize: 60, fontWeight: 700, letterSpacing: "-0.03em",
          }}>
            {initialsOf(name)}
          </div>
        )}
        {uploading && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(15,23,42,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span className="pf-spinner" style={{
              width: 24, height: 24, borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
            }} />
          </div>
        )}
        {!previewMode && (
          <button
            onClick={pickFile}
            disabled={uploading}
            aria-label="Change photo"
            style={{
              position: "absolute", bottom: 10, right: 10,
              padding: "7px 11px", borderRadius: 999, border: "none",
              background: "rgba(255,255,255,0.95)", backdropFilter: "blur(6px)",
              color: P.ink, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 12, fontWeight: 700, fontFamily: FF,
              boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
            }}>
            <Camera size={13} /> Change
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
               style={{ display: "none" }} />
      </div>

      {/* Name + contact */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h2 style={{
          margin: 0, fontSize: 20, fontWeight: 800, color: P.ink,
          letterSpacing: "-0.025em",
        }}>
          {name}
        </h2>
        {email && (
          <div style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
            color: P.inkMuted,
          }}>
            <Mail size={13} color={P.inkDim} /> {email}
          </div>
        )}
        {phone && (
          <div style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
            color: P.inkMuted,
          }}>
            <Phone size={13} color={P.inkDim} /> {phone}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <ActionTile
          icon={<Eye size={16} />}
          title={previewMode ? "Exit preview" : "View as a boss"}
          subtitle={previewMode ? "Back to editing" : "Preview your profile"}
          onClick={onTogglePreview}
          highlighted={previewMode}
        />
        <ActionTile
          icon={<BarChart3 size={16} />}
          title="Analytics"
          subtitle="Coming soon"
          onClick={() => toast("profile analytics — coming soon.", { icon: "📊" })}
        />
      </div>

      {/* Section nav */}
      <nav style={{
        display: "flex", flexDirection: "column", gap: 1,
        paddingTop: 6,
      }}>
        {SECTIONS.map(s => (
          <a key={s.id} href={`#${s.id}`}
             onClick={(e) => {
               e.preventDefault()
               document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })
             }}
             style={{
               padding: "7px 10px", borderRadius: 8,
               color: P.inkMuted, fontSize: 12.5, fontWeight: 500,
               textDecoration: "none", fontFamily: FF,
               display: "flex", alignItems: "center", gap: 8,
               transition: "background 0.1s",
             }}
             onMouseEnter={e => e.currentTarget.style.background = P.surfaceAlt}
             onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            {s.label}
          </a>
        ))}
      </nav>
    </aside>
  )
}

const SECTIONS = [
  { id: "job-prefs",      label: "Job Preferences" },
  { id: "prompts",        label: "Prompts" },
  { id: "social-links",   label: "Social Links" },
  { id: "work-experience",label: "Work Experience" },
  { id: "education",      label: "Education" },
  { id: "achievements",   label: "Achievements" },
  { id: "projects",       label: "Projects" },
  { id: "resume",         label: "Resume" },
]

function ActionTile({ icon, title, subtitle, onClick, highlighted }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", flexDirection: "column", alignItems: "flex-start",
      gap: 6, padding: "11px 13px", borderRadius: 12, border: "none",
      background: highlighted ? P.ink : P.pillBg,
      color: highlighted ? "#fff" : P.ink, cursor: "pointer",
      fontFamily: FF, textAlign: "left", letterSpacing: "-0.005em",
      transition: "background 0.12s",
    }}
    onMouseEnter={e => {
      if (highlighted) return
      e.currentTarget.style.background = "#EBEBEE"
    }}
    onMouseLeave={e => {
      if (highlighted) return
      e.currentTarget.style.background = P.pillBg
    }}
    >
      <span style={{ display: "inline-flex", color: highlighted ? "#fff" : P.inkMuted }}>
        {icon}
      </span>
      <div style={{ fontSize: 12.5, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 10.5, color: highlighted ? "rgba(255,255,255,0.75)" : P.inkDim, fontWeight: 500 }}>
        {subtitle}
      </div>
    </button>
  )
}

// ── Job preferences section + editor ─────────────────────────────────────
function SpecRow({ label, children, last }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: 12,
      padding: "11px 0",
      boxShadow: last ? "none" : `inset 0 -1px 0 ${P.divider}`,
    }}>
      <span style={{
        fontSize: 10.5, fontWeight: 700, color: P.inkDim,
        letterSpacing: "0.1em", textTransform: "uppercase",
        flexShrink: 0,
      }}>
        {label}
      </span>
      <span aria-hidden="true" style={{
        flex: 1, height: 0, minWidth: 16,
        alignSelf: "center", marginBottom: 3,
        borderBottom: `1.5px dotted ${P.inkFaint}`,
        opacity: 0.55,
      }} />
      <span style={{
        fontSize: 13.5, fontWeight: 700, color: P.ink,
        letterSpacing: "-0.015em", textAlign: "right",
        display: "inline-flex", alignItems: "center", gap: 8,
        flexWrap: "wrap", justifyContent: "flex-end",
      }}>
        {children}
      </span>
    </div>
  )
}

function JobPreferencesSection({ userData, previewMode, onEdit }) {
  const level = userData?.filters?.level || (Array.isArray(userData?.filters?.levels) ? userData.filters.levels[0] : "")
  const yoe = toInt(userData?.experience)
  const locs = (userData?.filters?.locations || []).map(prettyCity).filter(Boolean)
  const currCtcN = toInt(userData?.currSalary)
  const expCtcN  = toInt(userData?.expSalary)
  const currCtc  = lakhLabel(currCtcN)
  const expCtc   = lakhLabel(expCtcN)
  const notice   = userData?.noticePeriod || ""
  const roleTitle = userData?.currentJobTitle || userData?.currJobTitle || ""

  const yoeText   = yoe > 0 ? `${yoe}+ yrs` : ""
  const levelText = level ? shortLevel(level) : ""
  const subhead   = [levelText, yoeText].filter(Boolean).join(" · ")

  const hikePct = (currCtcN > 0 && expCtcN > 0)
    ? Math.round(((expCtcN - currCtcN) / currCtcN) * 100)
    : null

  const rows = []
  if (subhead) rows.push({ key: "level", label: "Level", value: subhead })
  if (currCtcN > 0 || expCtcN > 0) {
    rows.push({
      key: "comp", label: "Compensation",
      value: (
        <>
          <span style={{ color: currCtc ? P.ink : P.inkFaint }}>{currCtc || "—"}</span>
          <ArrowRight size={12} color={P.inkFaint} style={{ margin: "0 -2px" }} />
          <span style={{ color: expCtc ? P.ink : P.inkFaint }}>{expCtc || "—"}</span>
          {hikePct != null && (
            <span style={{
              padding: "1px 7px", borderRadius: 999,
              background: hikePct >= 0 ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)",
              color: hikePct >= 0 ? P.success : P.danger,
              fontSize: 10.5, fontWeight: 700, letterSpacing: "-0.005em",
            }}>
              {hikePct >= 0 ? `+${hikePct}%` : `${hikePct}%`}
            </span>
          )}
        </>
      ),
    })
  }
  if (locs.length > 0) {
    rows.push({ key: "loc", label: "Location", value: locs.join(" · ") })
  }
  if (notice) rows.push({ key: "notice", label: "Available", value: notice })

  return (
    <SectionCard
      id="job-prefs"
      eyebrow="What I want"
      title="Job preferences"
      subtitle="Role, salary, locations and timing — tune what we match you against."
      icon={<Briefcase size={16} />}
      action={!previewMode && (
        <GhostButton icon={<Pencil size={12} />} onClick={onEdit}>Edit</GhostButton>
      )}
    >
      <div>
        {/* Role header */}
        <div style={{ marginBottom: 4 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: P.inkFaint,
            letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 5,
          }}>
            Looking for
          </div>
          <div style={{
            fontSize: 20, fontWeight: 800,
            color: roleTitle ? P.ink : P.inkFaint,
            letterSpacing: "-0.03em", lineHeight: 1.2,
          }}>
            {roleTitle || "Role not set yet"}
          </div>
        </div>

        {rows.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {rows.map((r, i) => (
              <SpecRow key={r.key} label={r.label} last={i === rows.length - 1}>
                {r.value}
              </SpecRow>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  )
}

export function JobPreferencesModal({ userData, userId, onClose }) {
  const [roleTitle, setRoleTitle] = useState(userData?.currentJobTitle || "")
  const [yoe, setYoe] = useState(toInt(userData?.experience))
  const [level, setLevel] = useState(userData?.filters?.level || "")
  const [locations, setLocations] = useState(() => [...(userData?.filters?.locations || [])])
  const [currSal, setCurrSal] = useState(indianFormatInput(userData?.currSalary || ""))
  const [expSal, setExpSal]   = useState(indianFormatInput(userData?.expSalary || ""))
  const [notice, setNotice]   = useState(userData?.noticePeriod || "")
  const [saving, setSaving]   = useState(false)

  const toggleLoc = (k) => {
    setLocations(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])
  }

  const save = async () => {
    if (!userId || saving) return
    setSaving(true)
    try {
      await setDoc(fsDoc(db, "users", userId), {
        currentJobTitle: roleTitle.trim(),
        experience: toInt(yoe),
        noticePeriod: notice,
        currSalary: toInt(currSal),
        expSalary: toInt(expSal),
        filters: {
          level: level || null,
          locations,
        },
      }, { merge: true })
      toast.success("preferences saved")
      onClose?.()
    } catch (err) {
      console.error("Save prefs failed:", err)
      toast.error("couldn't save. try again.")
    } finally { setSaving(false) }
  }

  const field = (label, control) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: P.inkDim,
        letterSpacing: "0.04em", textTransform: "uppercase",
      }}>
        {label}
      </span>
      {control}
    </label>
  )

  const inputStyle = {
    width: "100%", padding: "10px 13px", borderRadius: 10,
    border: "none", background: P.pillBg, color: P.ink,
    fontSize: 13.5, fontFamily: FF, outline: "none", boxSizing: "border-box",
  }

  return (
    <Modal title="Edit job preferences" onClose={onClose} maxWidth={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {field("Role title", (
          <input value={roleTitle} onChange={e => setRoleTitle(e.target.value)}
            placeholder="e.g. Senior Frontend Engineer" style={inputStyle} />
        ))}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {field("Years of experience", (
            <input type="number" min={0} max={60} value={yoe}
              onChange={e => setYoe(e.target.value)} style={inputStyle} />
          ))}
          {field("Level", (
            <select value={level} onChange={e => setLevel(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="JUNIOR">Junior (0-2 yrs)</option>
              <option value="MID">Mid-level (3-5 yrs)</option>
              <option value="SENIOR">Senior (6-9 yrs)</option>
              <option value="LEAD">Lead</option>
              <option value="PRINCIPAL">Principal</option>
            </select>
          ))}
        </div>

        {field("Preferred locations", (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {CITY_KEYS.map((k, i) => {
              const active = locations.includes(k)
              return (
                <button key={k} onClick={() => toggleLoc(k)} style={{
                  padding: "7px 12px", borderRadius: 999, border: "none",
                  background: active ? P.ink : P.pillBg,
                  color: active ? "#fff" : P.ink2, cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: FF,
                  transition: "background 0.12s",
                }}>
                  {CITY_NAMES[i]}
                </button>
              )
            })}
          </div>
        ))}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {field("Current CTC (₹/yr)", (
            <input value={currSal} onChange={e => setCurrSal(indianFormatInput(e.target.value))}
              placeholder="12,00,000" style={inputStyle} />
          ))}
          {field("Expected CTC (₹/yr)", (
            <input value={expSal} onChange={e => setExpSal(indianFormatInput(e.target.value))}
              placeholder="18,00,000" style={inputStyle} />
          ))}
        </div>

        {field("Notice period", (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {NOTICE_PERIODS.map(n => {
              const active = notice === n
              return (
                <button key={n} onClick={() => setNotice(active ? "" : n)} style={{
                  padding: "7px 12px", borderRadius: 999, border: "none",
                  background: active ? P.ink : P.pillBg,
                  color: active ? "#fff" : P.ink2, cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: FF,
                  transition: "background 0.12s",
                }}>
                  {n}
                </button>
              )
            })}
          </div>
        ))}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}

// ── Prompts section + editor ─────────────────────────────────────────────
function PromptsSection({ userData, previewMode, onAdd, onEdit, onDelete }) {
  const prompts = (userData?.prompts || []).filter(p => p?.prompt && p?.answer)
  return (
    <SectionCard
      id="prompts"
      eyebrow="Your voice"
      title="Prompts"
      subtitle="Short answers bosses actually read before deciding to reach out."
      icon={<Quote size={16} />}
      count={prompts.length}
      action={!previewMode && (
        <GhostButton icon={<Plus size={13} />} onClick={() => onAdd(prompts.length)}>Add</GhostButton>
      )}
    >
      {prompts.length === 0 ? (
        <EmptyHint
          icon={<Quote size={18} color={P.inkFaint} />}
          text="Answer a prompt or two — these show up on your public profile."
          hint="Bosses often read these before deciding to DM."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {prompts.map((p, i) => (
            <div key={i} style={{
              position: "relative", padding: "16px 18px 16px 48px",
              background: P.pillBg, borderRadius: 14,
            }}>
              <Quote size={18} style={{
                position: "absolute", top: 17, left: 18,
                color: P.inkFaint, opacity: 0.85,
              }} />
              <div style={{
                fontSize: 11, fontWeight: 700, color: P.inkDim,
                letterSpacing: "0.08em", textTransform: "uppercase",
                marginBottom: 7,
                paddingRight: previewMode ? 0 : 64,
              }}>
                {p.prompt}
              </div>
              <div style={{
                fontSize: 14, color: P.ink2, lineHeight: 1.6, whiteSpace: "pre-wrap",
                fontWeight: 500,
              }}>
                {p.answer}
              </div>
              {!previewMode && (
                <div style={{
                  position: "absolute", top: 12, right: 12, display: "flex", gap: 4,
                }}>
                  <button onClick={() => onEdit(i)} aria-label="Edit prompt" style={iconBtnStyle}
                    onMouseEnter={e => e.currentTarget.style.background = "#fff"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.75)"}>
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => onDelete(i)} aria-label="Delete prompt"
                    style={{ ...iconBtnStyle, color: P.danger }}
                    onMouseEnter={e => e.currentTarget.style.background = "#FEE2E2"}
                    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.75)"}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

const iconBtnStyle = {
  width: 26, height: 26, borderRadius: 7, border: "none",
  background: "rgba(255,255,255,0.75)", color: P.inkMuted, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.12s",
}

export function PromptEditorModal({ initial, onSave, onClose, usedPrompts = [] }) {
  const [prompt, setPrompt] = useState(initial?.prompt || "")
  const [answer, setAnswer] = useState(initial?.answer || "")
  const [saving, setSaving] = useState(false)

  const availablePrompts = useMemo(() => {
    const inUse = new Set(usedPrompts)
    if (initial?.prompt) inUse.delete(initial.prompt)
    return PROMPT_SUGGESTIONS.filter(p => !inUse.has(p))
  }, [usedPrompts, initial])

  const canSave = prompt.trim() && answer.trim() && !saving

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    try { await onSave({ prompt: prompt.trim(), answer: answer.trim() }) }
    finally { setSaving(false) }
  }

  return (
    <Modal title={initial?.prompt ? "Edit prompt" : "Add a prompt"} onClose={onClose} maxWidth={600}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: P.inkDim,
            letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6,
          }}>
            Prompt
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availablePrompts.map(s => (
              <button key={s} onClick={() => setPrompt(s)} style={{
                padding: "7px 12px", borderRadius: 999, border: "none",
                background: prompt === s ? P.ink : P.pillBg,
                color: prompt === s ? "#fff" : P.ink2, cursor: "pointer",
                fontSize: 12, fontWeight: 500, fontFamily: FF, textAlign: "left",
                transition: "background 0.12s",
              }}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: P.inkDim,
            letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6,
          }}>
            Your answer
          </div>
          <textarea
            value={answer} onChange={e => setAnswer(e.target.value)}
            maxLength={600}
            placeholder="Keep it specific, honest, under 150 words."
            style={{
              width: "100%", minHeight: 150, resize: "vertical",
              padding: 14, borderRadius: 12, border: "none",
              background: P.pillBg,
              color: P.ink, fontFamily: FF, fontSize: 14, lineHeight: 1.55,
              outline: "none", boxSizing: "border-box",
            }}
          />
          <div style={{
            marginTop: 4, fontSize: 11, color: P.inkFaint, textAlign: "right",
          }}>
            {answer.length}/600
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={!canSave}>
            {saving ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}

// ── Social brand icon (mirrors mobile asset-based icons) ──────────────────
function CodeforcesIcon({ size = 20 }) {
  // Three colored bars — matches mobile _SocialIcon.codeforces
  const bar = (w, h, color, left) => (
    <span style={{
      position: "absolute", bottom: 0, left,
      width: w, height: h, background: color, borderRadius: 1,
    }} />
  )
  const base = size
  return (
    <span style={{
      position: "relative", display: "inline-block", width: base, height: base,
    }}>
      {bar(base * 0.13, base * 0.55, "#C9A129", base * 0.18)}
      {bar(base * 0.13, base * 0.80, "#3077BD", base * 0.44)}
      {bar(base * 0.13, base * 0.40, "#B24949", base * 0.70)}
    </span>
  )
}

function SocialIcon({ type, size = 20 }) {
  const img = (src, fit = "contain") => (
    <img src={src} alt="" width={size} height={size}
         style={{ objectFit: fit, display: "block" }} />
  )
  switch (type) {
    case "github":     return img(githubSvg)
    case "linkedin":   return img(linkedinPng)
    case "leetcode":   return img(leetcodePng)
    case "medium":     return img(mediumPng)
    case "dribbble":   return img(dribbblePng)
    case "youtube":    return img(youtubePng)
    case "codeforces": return <CodeforcesIcon size={size} />
    case "portfolio":
    default:
      return <Link2 size={size} strokeWidth={2} />
  }
}

function GitHubPreview({ profile }) {
  if (!profile) return null
  const avatar = profile.avatarUrl || profile.avatar_url
  const name = profile.name || profile.login
  const bio = profile.bio
  const followers = profile.followers || 0
  const repoCount = profile.publicRepositories ?? profile.public_repos ?? 0
  return (
    <div style={{
      marginTop: 12, padding: "10px 12px", borderRadius: 10,
      background: "#fff",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      {avatar && (
        <img src={avatar} alt="" width={34} height={34}
             style={{ borderRadius: "50%" }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: P.ink,
          letterSpacing: "-0.01em",
        }}>
          {name}
        </div>
        {bio && (
          <div style={{
            fontSize: 11.5, color: P.inkMuted, marginTop: 1,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {bio}
          </div>
        )}
      </div>
      <div style={{
        display: "flex", gap: 10, fontSize: 11, color: P.inkDim, fontWeight: 600,
        flexShrink: 0,
      }}>
        <span>{Number(followers).toLocaleString()} followers</span>
        <span>{Number(repoCount).toLocaleString()} repos</span>
      </div>
    </div>
  )
}

function SocialLinksSection({ userData, previewMode, onEdit }) {
  const links = userData?.socialLinks || {}
  const connected = SOCIAL_TYPES.filter(t => links[t.k])
  const unconnected = SOCIAL_TYPES.filter(t => !links[t.k])

  const handleOf = (type, url) => {
    try {
      const u = new URL(url)
      const first = stripSocialPathPrefix(type, u.pathname).split("/").filter(Boolean)[0] || ""
      return first ? `@${first}` : u.host.replace(/^www\./, "")
    } catch { return url }
  }

  return (
    <SectionCard
      id="social-links"
      eyebrow="Links"
      title="Social presence"
      subtitle={connected.length === 0
        ? "Add your handles so bosses can see your work."
        : `${connected.length} of ${SOCIAL_TYPES.length} connected.`}
      icon={<Hash size={14} />}
    >
      {connected.length === 0 ? (
        <div style={{
          padding: "22px 18px", borderRadius: 14, background: P.pillBg,
          display: "flex", gap: 14, alignItems: "center",
        }}>
          <span style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Hash size={18} color={P.inkMuted} />
          </span>
          <div>
            <div style={{ fontSize: 13.5, color: P.ink, fontWeight: 700 }}>
              No social links yet.
            </div>
            <div style={{ marginTop: 2, fontSize: 12, color: P.inkDim }}>
              Pick one below — bosses often skim these before reaching out.
            </div>
          </div>
        </div>
      ) : (
        <div
          className="pf-social-grid"
          style={{ marginBottom: unconnected.length > 0 && !previewMode ? 18 : 0 }}
        >
          {connected.map(t => {
            const url = links[t.k]
            const label = handleOf(t.k, url)
            const profile = t.k === "github" ? links.githubProfile : null
            return (
              <div
                key={t.k}
                onClick={() => !previewMode && onEdit(t.k)}
                role={previewMode ? undefined : "button"}
                style={{
                  position: "relative", padding: "14px 14px 12px",
                  borderRadius: 14, background: P.pillBg,
                  cursor: previewMode ? "default" : "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (!previewMode) e.currentTarget.style.background = "#EBEBEE" }}
                onMouseLeave={e => { e.currentTarget.style.background = P.pillBg }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <span style={{
                    width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                    background: "#fff", color: t.color,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <SocialIcon type={t.k} size={20} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 700, color: P.ink,
                      display: "flex", alignItems: "center", gap: 6,
                      letterSpacing: "-0.01em",
                    }}>
                      {t.label}
                      <span aria-hidden="true" style={{
                        width: 6, height: 6, borderRadius: "50%", background: P.success,
                      }} />
                    </div>
                    <div style={{
                      fontSize: 11.5, color: P.inkMuted,
                      marginTop: 2, whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis", fontFamily: FF,
                    }}>
                      {label}
                    </div>
                  </div>
                  <a href={url} target="_blank" rel="noreferrer"
                     onClick={e => e.stopPropagation()}
                     aria-label={`Open ${t.label}`}
                     style={{
                       width: 28, height: 28, borderRadius: 8,
                       background: "#fff", color: P.inkMuted, flexShrink: 0,
                       display: "inline-flex", alignItems: "center", justifyContent: "center",
                       transition: "background 0.12s",
                     }}
                     onMouseEnter={e => e.currentTarget.style.background = "#F7F7F8"}
                     onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>
                {t.k === "github" && profile && <GitHubPreview profile={profile} />}
              </div>
            )
          })}
        </div>
      )}

      {unconnected.length > 0 && !previewMode && (
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: P.inkFaint,
            letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 10,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span>Add more</span>
            <span style={{
              flex: 1, height: 1, background: P.divider,
            }} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {unconnected.map(t => (
              <button key={t.k} onClick={() => onEdit(t.k)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  padding: "7px 12px 7px 9px", borderRadius: 999, border: "none",
                  background: P.pillBg, color: P.ink2,
                  cursor: "pointer", fontFamily: FF,
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#EBEBEE"}
                onMouseLeave={e => e.currentTarget.style.background = P.pillBg}
              >
                <span style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  background: "#fff", display: "inline-flex",
                  alignItems: "center", justifyContent: "center",
                  opacity: 0.85,
                }}>
                  <SocialIcon type={t.k} size={12} />
                </span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</span>
                <Plus size={11} color={P.inkDim} strokeWidth={2.5} />
              </button>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  )
}

// ── Social profile panels (full-fidelity previews) ──────────────────────
function parseContribMap(raw) {
  if (!raw || typeof raw !== "object") return {}
  const out = {}
  for (const [k, v] of Object.entries(raw)) {
    const d = new Date(k)
    if (isNaN(d.getTime())) continue
    const key = d.toISOString().slice(0, 10)
    const n = Number(v) || 0
    if (n > (out[key] || 0)) out[key] = n
  }
  return out
}

function buildContribGrid(levelsMap) {
  const COLS = 16, ROWS = 7
  const today = new Date()
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))
  const endSat = new Date(todayUTC)
  endSat.setUTCDate(endSat.getUTCDate() + (6 - endSat.getUTCDay()))
  const startSun = new Date(endSat)
  startSun.setUTCDate(startSun.getUTCDate() - COLS * ROWS + 1)

  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(null))
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      const d = new Date(startSun)
      d.setUTCDate(d.getUTCDate() + c * ROWS + r)
      if (d > todayUTC) continue
      const key = d.toISOString().slice(0, 10)
      grid[r][c] = levelsMap[key] || 0
    }
  }
  return grid
}

const GH_HEATMAP_COLORS = ["#F0F1F6", "#D7FFF0", "#8CE2BA", "#11B872", "#215942"]

function ContribHeatmap({ levels }) {
  const grid = useMemo(() => buildContribGrid(levels), [levels])
  const dayLabels = ["", "Mon", "", "Wed", "", "Fri", ""]
  return (
    <div style={{
      padding: "12px 14px", borderRadius: 12, background: "#fff",
    }}>
      <div style={{
        display: "grid", gridTemplateColumns: "26px repeat(16, 1fr)", gap: 3,
      }}>
        {grid.map((row, r) => (
          <Fragment key={r}>
            <span style={{
              fontSize: 9.5, color: P.inkFaint, fontWeight: 600,
              display: "flex", alignItems: "center",
            }}>
              {dayLabels[r]}
            </span>
            {row.map((lvl, c) => (
              <div key={c} style={{
                aspectRatio: "1 / 1",
                borderRadius: 3,
                background: lvl == null ? "transparent" : GH_HEATMAP_COLORS[Math.min(4, Math.max(0, lvl))],
              }} />
            ))}
          </Fragment>
        ))}
      </div>
      <div style={{
        marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between",
        fontSize: 10, color: P.inkFaint, fontWeight: 600,
      }}>
        <span>16 weeks</span>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span>Less</span>
          {GH_HEATMAP_COLORS.map((c, i) => (
            <span key={i} style={{
              width: 10, height: 10, borderRadius: 3, background: c,
            }} />
          ))}
          <span>More</span>
        </div>
      </div>
    </div>
  )
}

function PanelHeader({ type, label, handle, url, accent }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 11,
    }}>
      <span style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: "#fff", color: accent,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <SocialIcon type={type} size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 800, color: P.ink,
          letterSpacing: "-0.01em",
        }}>
          {label}
        </div>
        {handle && (
          <div style={{
            fontSize: 11.5, color: P.inkMuted, marginTop: 1,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {handle}
          </div>
        )}
      </div>
      {url && (
        <a href={url} target="_blank" rel="noreferrer" aria-label={`Open ${label}`}
           onClick={e => e.stopPropagation()}
           style={{
             width: 28, height: 28, borderRadius: 8, flexShrink: 0,
             background: "#fff", color: P.inkMuted,
             display: "inline-flex", alignItems: "center", justifyContent: "center",
           }}>
          <ExternalLink size={13} />
        </a>
      )}
    </div>
  )
}

function GitHubPanel({ profile }) {
  if (!profile) return null
  const login = profile.login || ""
  const avatarUrl = profile.avatarUrl || profile.avatar_url || ""
  const name = profile.name || login
  const bio = profile.bio || ""
  const url = profile.url || profile.html_url || (login ? `https://github.com/${login}` : "")
  const followers = profile.followers ?? 0
  const repoCount = profile.publicRepositories ?? profile.public_repos ?? 0
  const yearlyContribs = profile.yearlyContributions
  const levels = parseContribMap(profile.contributionLevels)
  const hasHeatmap = Object.keys(levels).length > 0

  return (
    <div style={{
      padding: 16, borderRadius: 14, background: P.pillBg,
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <PanelHeader type="github" label="GitHub" handle={`@${login}`} url={url} accent={P.github} />

      {/* Identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {avatarUrl && (
          <img src={avatarUrl} alt="" width={44} height={44}
               style={{ borderRadius: "50%", flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: P.ink,
            letterSpacing: "-0.01em",
          }}>
            {name}
          </div>
          {bio && (
            <div style={{
              fontSize: 11.5, color: P.inkMuted, marginTop: 1, lineHeight: 1.4,
            }}>
              {bio}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 14,
        fontSize: 12, color: P.inkDim, fontWeight: 500,
      }}>
        {yearlyContribs != null && (
          <span>
            <b style={{ color: P.ink, fontWeight: 800 }}>{yearlyContribs.toLocaleString()}</b> contributions
          </span>
        )}
        <span>
          <b style={{ color: P.ink, fontWeight: 800 }}>{Number(followers).toLocaleString()}</b> followers
        </span>
        <span>
          <b style={{ color: P.ink, fontWeight: 800 }}>{Number(repoCount).toLocaleString()}</b> repos
        </span>
      </div>

      {hasHeatmap && <ContribHeatmap levels={levels} />}
    </div>
  )
}

function AcceptanceRing({ fractions, solved, total }) {
  const SW = 12
  const rings = [
    { r: 118, color: "#B44747", track: "#F6E4E1", fraction: fractions.hard || 0 },
    { r: 96,  color: "#F97316", track: "#FFE4CF", fraction: fractions.medium || 0 },
    { r: 74,  color: "#3176BB", track: "#E1EBF5", fraction: fractions.easy || 0 },
  ]
  const maxR = rings[0].r + SW / 2 + 2
  const W = maxR * 2
  const H = maxR + SW
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%"
         style={{ maxWidth: 320, display: "block", margin: "0 auto" }}>
      <g transform={`translate(${maxR}, ${maxR}) rotate(180)`}>
        {rings.map((ring, i) => {
          const half = Math.PI * ring.r
          return (
            <Fragment key={i}>
              <circle cx={0} cy={0} r={ring.r} fill="none"
                stroke={ring.track} strokeWidth={SW} strokeLinecap="round"
                strokeDasharray={`${half} ${half * 2}`} />
              <circle cx={0} cy={0} r={ring.r} fill="none"
                stroke={ring.color} strokeWidth={SW} strokeLinecap="round"
                strokeDasharray={`${half * Math.max(0.001, Math.min(1, ring.fraction))} ${half * 2}`} />
            </Fragment>
          )
        })}
      </g>
      <text x={maxR} y={maxR - 4} textAnchor="middle" style={{
        fontSize: 26, fontWeight: 800, fill: P.ink, letterSpacing: "-0.03em",
      }}>
        {solved}
        <tspan style={{ fontSize: 15, fontWeight: 700, fill: P.inkDim }}> / {total}</tspan>
      </text>
      <text x={maxR} y={maxR + 15} textAnchor="middle" style={{
        fontSize: 10.5, fontWeight: 700, fill: P.inkDim, letterSpacing: "0.14em",
      }}>
        SOLVED
      </text>
    </svg>
  )
}

function LeetCodePanel({ profile }) {
  if (!profile) return null
  const username = profile.username || ""
  const url = profile.url || (username ? `https://leetcode.com/u/${username}` : "")
  const ranking = profile.ranking
  const diffs = profile.difficulties || {}
  const easy   = diffs.Easy   || diffs.easy   || {}
  const medium = diffs.Medium || diffs.medium || {}
  const hard   = diffs.Hard   || diffs.hard   || {}

  const totalSolved = profile.solved
    ?? ((easy.solved || 0) + (medium.solved || 0) + (hard.solved || 0))
  const totalTotal = profile.total
    ?? ((easy.total || 0) + (medium.total || 0) + (hard.total || 0))

  const frac = (d) => (d.total > 0 ? (d.solved || 0) / d.total : 0)
  const fractions = { easy: frac(easy), medium: frac(medium), hard: frac(hard) }

  const colOf = (label, color, d) => (
    <div style={{
      padding: "12px 10px", borderRadius: 12, background: "#fff",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, color, letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16, fontWeight: 800, color: P.ink, letterSpacing: "-0.02em",
      }}>
        {d.solved || 0}
        <span style={{ fontSize: 12, fontWeight: 600, color: P.inkDim }}>
          /{d.total || 0}
        </span>
      </div>
      {d.beatsPercent != null && (
        <div style={{ fontSize: 10, color: P.inkDim, fontWeight: 600 }}>
          Beats {Math.round(d.beatsPercent)}%
        </div>
      )}
    </div>
  )

  return (
    <div style={{
      padding: 16, borderRadius: 14, background: P.pillBg,
      display: "flex", flexDirection: "column", gap: 16,
    }}>
      <PanelHeader type="leetcode" label="LeetCode" handle={`@${username}`} url={url} accent={P.leetcode} />

      {ranking != null && (
        <div style={{
          fontSize: 11.5, color: P.inkDim, fontWeight: 600,
        }}>
          Rank <b style={{ color: P.ink, fontWeight: 800 }}>#{Number(ranking).toLocaleString()}</b>
        </div>
      )}

      <AcceptanceRing fractions={fractions} solved={totalSolved} total={totalTotal} />

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8,
      }}>
        {colOf("Easy", "#3176BB", easy)}
        {colOf("Medium", "#F97316", medium)}
        {colOf("Hard", "#B44747", hard)}
      </div>
    </div>
  )
}

function RatingLineChart({ history }) {
  const points = Array.isArray(history) ? history : []
  if (points.length < 2) return null

  const W = 500, H = 160
  const padL = 10, padR = 42, padT = 20, padB = 24

  const times = points.map(p => {
    const d = new Date(p.date)
    return isNaN(d.getTime()) ? 0 : d.getTime()
  })
  const ratings = points.map(p => Number(p.rating) || 0)
  const minT = Math.min(...times), maxT = Math.max(...times)
  const minR = Math.min(...ratings), maxR = Math.max(...ratings)
  const yLow = Math.max(0, minR - 200)
  const yHigh = maxR + 200
  const sx = (t) => padL + ((t - minT) / Math.max(1, (maxT - minT))) * (W - padL - padR)
  const sy = (r) => padT + ((yHigh - r) / Math.max(1, (yHigh - yLow))) * (H - padT - padB)

  const sampled = points.length > 12
    ? Array.from({ length: 12 }, (_, i) => points[Math.round(i * (points.length - 1) / 11)])
    : points

  const path = sampled.map((p, i) => {
    const x = sx(times[points.indexOf(p)]).toFixed(1)
    const y = sy(Number(p.rating)).toFixed(1)
    return `${i === 0 ? "M" : "L"} ${x} ${y}`
  }).join(" ")

  const fmtDate = (ts) => {
    const d = new Date(ts)
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
  }

  return (
    <div style={{
      padding: "12px 12px 8px", borderRadius: 12, background: "#fff",
    }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%"
           preserveAspectRatio="none"
           style={{ display: "block", height: H }}>
        <path d={path} fill="none" stroke="#F4511E" strokeWidth={2.5}
              strokeLinejoin="round" strokeLinecap="round" />
        {sampled.map((p, i) => {
          const x = sx(times[points.indexOf(p)])
          const y = sy(Number(p.rating))
          return (
            <circle key={i} cx={x} cy={y} r={3.4}
              fill="#fff" stroke="#F4511E" strokeWidth={1.6} />
          )
        })}
        <text x={W - padR + 4} y={sy(maxR) + 3} fontSize={10}
              fill={P.inkFaint} fontWeight={700}>
          {maxR}
        </text>
        <text x={W - padR + 4} y={sy(minR) + 3} fontSize={10}
              fill={P.inkFaint} fontWeight={700}>
          {minR}
        </text>
      </svg>
      <div style={{
        marginTop: 4, display: "flex", justifyContent: "space-between",
        fontSize: 10, color: P.inkFaint, fontWeight: 600,
      }}>
        <span>{fmtDate(minT)}</span>
        <span>{fmtDate(maxT)}</span>
      </div>
    </div>
  )
}

function CodeforcesPanel({ profile }) {
  if (!profile) return null
  const handle = profile.handle || ""
  const url = profile.url || (handle ? `https://codeforces.com/profile/${handle}` : "")
  const rating = profile.rating
  const maxRating = profile.maxRating
  const maxJump = profile.maxJump
  const solved = profile.solvedProblems ?? profile.solved ?? 0
  const history = profile.history || []

  const stat = (label, val, color) => (
    <div style={{
      padding: "11px 12px", borderRadius: 12, background: "#fff",
      flex: 1, minWidth: 0,
    }}>
      <div style={{
        fontSize: 9.5, fontWeight: 700, color: P.inkFaint,
        letterSpacing: "0.14em", textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 17, fontWeight: 800, color: color || P.ink,
        letterSpacing: "-0.025em", marginTop: 3,
      }}>
        {val}
      </div>
    </div>
  )

  return (
    <div style={{
      padding: 16, borderRadius: 14, background: P.pillBg,
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <PanelHeader type="codeforces" label="Codeforces" handle={`@${handle}`} url={url} accent={P.codeforces} />

      <div style={{ display: "flex", gap: 8 }}>
        {rating != null && stat("Current", rating)}
        {maxRating != null && stat("Max", maxRating)}
        {maxJump != null && stat("Max jump", `+${maxJump}`, "#16A34A")}
      </div>

      {history.length >= 2 && <RatingLineChart history={history} />}

      {solved > 0 && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 12, color: P.ink2, fontWeight: 600,
        }}>
          <TrendingUp size={13} color={P.inkDim} />
          <b style={{ color: P.ink, fontWeight: 800 }}>
            {Number(solved).toLocaleString()}
          </b>
          problems solved
        </div>
      )}
    </div>
  )
}

function MediumPanel({ profile }) {
  if (!profile) return null
  const username = profile.username || ""
  const url = profile.url || (username ? `https://medium.com/@${username}` : "")
  const articles = (profile.articles || []).slice(0, 6)

  const fmtDate = (iso) => {
    if (!iso) return ""
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ""
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  return (
    <div style={{
      padding: 16, borderRadius: 14, background: P.pillBg,
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <PanelHeader type="medium" label="Medium" handle={`@${username}`} url={url} accent={P.medium} />

      {articles.length === 0 ? (
        <div style={{
          padding: "14px 16px", borderRadius: 10, background: "#fff",
          fontSize: 12.5, color: P.inkMuted,
        }}>
          Articles will appear once we fetch your feed.
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}>
          {articles.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer"
               onClick={e => e.stopPropagation()}
               style={{
                 textDecoration: "none", color: "inherit",
                 borderRadius: 12, background: "#fff", overflow: "hidden",
                 display: "flex", flexDirection: "column",
               }}>
              {a.imageUrl && (
                <div style={{
                  width: "100%", aspectRatio: "16 / 9",
                  background: `#F3F2ED url(${a.imageUrl}) center / cover no-repeat`,
                }} />
              )}
              <div style={{ padding: "12px 14px", flex: 1 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: P.ink,
                  lineHeight: 1.3, letterSpacing: "-0.01em",
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}>
                  {a.title}
                </div>
                {(a.tags && a.tags.length > 0) && (
                  <div style={{
                    marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4,
                  }}>
                    {a.tags.slice(0, 3).map((tag, j) => (
                      <span key={j} style={{
                        padding: "2px 7px", borderRadius: 999, background: P.pillBg,
                        fontSize: 10, fontWeight: 600, color: P.inkMuted,
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{
                  marginTop: 10, fontSize: 10.5, color: P.inkFaint, fontWeight: 500,
                }}>
                  {[fmtDate(a.publishedAt), a.readMinutes ? `${a.readMinutes} min read` : ""]
                    .filter(Boolean).join(" · ")}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

const PROFILE_KEY_BY_TYPE = {
  github:     "githubProfile",
  leetcode:   "leetcodeProfile",
  codeforces: "codeforcesProfile",
  medium:     "mediumProfile",
}

function stripSocialPathPrefix(type, pathStr) {
  const t = SOCIAL_MAP[type]
  if (!t) return pathStr
  let s = pathStr.replace(/^\/+/, "")
  if (t.host) {
    const pathPref = t.prefix.replace(t.host + "/", "")
    if (pathPref && s.toLowerCase().startsWith(pathPref.toLowerCase())) {
      s = s.slice(pathPref.length)
    }
  }
  s = s.replace(/^@/, "")
  return s
}

export function SocialLinkModal({ type, userData, userId, onClose }) {
  const t = SOCIAL_MAP[type]
  const current = userData?.socialLinks?.[type] || ""
  const [raw, setRaw] = useState(() => {
    if (!current) return ""
    try {
      const path = new URL(current).pathname
      return stripSocialPathPrefix(type, path) || current
    } catch { return current }
  })
  const [saving, setSaving] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verifyErr, setVerifyErr] = useState(null)
  const [gh, setGh] = useState(userData?.socialLinks?.githubProfile || null)
  const [lc, setLc] = useState(userData?.socialLinks?.leetcodeProfile || null)
  const [cf, setCf] = useState(userData?.socialLinks?.codeforcesProfile || null)
  const [md, setMd] = useState(userData?.socialLinks?.mediumProfile || null)

  const normalized = useMemo(() => normalizeSocialUrl(type, raw), [type, raw])

  const handleFromNormalized = () => {
    if (!normalized) return ""
    try {
      const u = new URL(normalized)
      const parts = u.pathname.split("/").filter(Boolean)
      if (type === "medium") {
        return (parts[0] || "").replace(/^@/, "")
      }
      return (parts[parts.length - 1] || "").replace(/^@/, "")
    } catch { return "" }
  }

  const verifyGithub = async () => {
    const login = handleFromNormalized()
    if (!login) return
    setVerifying(true); setVerifyErr(null)
    try {
      const [userR, contribR] = await Promise.all([
        fetch(`https://api.github.com/users/${encodeURIComponent(login)}`),
        fetch(`https://github-contributions-api.jogruber.de/v4/${encodeURIComponent(login)}?y=last`),
      ])
      if (userR.status === 404) { setVerifyErr("No GitHub user with that handle"); setGh(null); return }
      if (!userR.ok) throw new Error(`HTTP ${userR.status}`)
      const user = await userR.json()

      let contributionLevels = {}
      let yearlyContributions = null
      if (contribR.ok) {
        try {
          const cdata = await contribR.json()
          for (const c of (cdata.contributions || [])) {
            if (c?.date && c?.level != null) {
              contributionLevels[c.date] = c.level
            }
          }
          if (cdata.total && typeof cdata.total === "object") {
            yearlyContributions =
              cdata.total.lastYear
              ?? Object.values(cdata.total).reduce((a, b) => a + (Number(b) || 0), 0)
              ?? null
          }
        } catch (e) {
          console.warn("Contributions parse failed:", e)
        }
      }

      setGh({
        login: user.login,
        url: user.html_url || normalized,
        avatarUrl: user.avatar_url || "",
        name: user.name || "",
        bio: user.bio || "",
        followers: user.followers || 0,
        publicRepositories: user.public_repos || 0,
        yearlyContributions,
        contributionLevels,
      })
    } catch (err) {
      console.error("GitHub verify failed:", err)
      setVerifyErr("Couldn't reach GitHub")
    } finally { setVerifying(false) }
  }

  const verifyCodeforces = async () => {
    const handle = handleFromNormalized()
    if (!handle) return
    setVerifying(true); setVerifyErr(null)
    try {
      const [infoR, ratingR, statusR] = await Promise.all([
        fetch(`https://codeforces.com/api/user.info?handles=${encodeURIComponent(handle)}`),
        fetch(`https://codeforces.com/api/user.rating?handle=${encodeURIComponent(handle)}`),
        fetch(`https://codeforces.com/api/user.status?handle=${encodeURIComponent(handle)}`),
      ])
      const info = await infoR.json()
      if (info.status !== "OK") {
        setVerifyErr(info.comment || "Couldn't find that handle")
        setCf(null); return
      }
      const user = info.result?.[0] || {}
      const rating = ratingR.ok ? await ratingR.json() : { result: [] }
      const status = statusR.ok ? await statusR.json() : { result: [] }
      const history = (rating.result || []).map(x => ({
        date: new Date((x.ratingUpdateTimeSeconds || 0) * 1000).toISOString(),
        rating: x.newRating || 0,
      }))
      let maxJump = 0
      for (const r of (rating.result || [])) {
        const diff = (r.newRating || 0) - (r.oldRating || 0)
        if (diff > maxJump) maxJump = diff
      }
      const solvedSet = new Set()
      for (const s of (status.result || [])) {
        if (s.verdict === "OK" && s.problem) {
          solvedSet.add(`${s.problem.contestId || s.problem.problemsetName || "?"}-${s.problem.index || ""}`)
        }
      }
      setCf({
        handle: user.handle,
        url: `https://codeforces.com/profile/${user.handle}`,
        rating: user.rating ?? null,
        maxRating: user.maxRating ?? null,
        maxJump: maxJump || null,
        solvedProblems: solvedSet.size,
        history,
      })
    } catch (err) {
      console.error("Codeforces verify failed:", err)
      setVerifyErr("Couldn't reach Codeforces")
    } finally { setVerifying(false) }
  }

  const verifyLeetcode = async () => {
    const username = handleFromNormalized()
    if (!username) return
    setVerifying(true); setVerifyErr(null)
    try {
      const r = await fetch("https://leetcode.com/graphql/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `query userProfile($username: String!) {
            matchedUser(username: $username) {
              username
              profile { ranking }
              submitStatsGlobal { acSubmissionNum { difficulty count } }
              problemsSolvedBeatsStats { difficulty percentage }
            }
            allQuestionsCount { difficulty count }
          }`,
          variables: { username },
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      if (data.errors || !data.data?.matchedUser) {
        setVerifyErr("Couldn't find that LeetCode user")
        setLc(null); return
      }
      const user = data.data.matchedUser
      const totals = Object.fromEntries(
        (data.data.allQuestionsCount || []).map(x => [x.difficulty, x.count])
      )
      const acNums = Object.fromEntries(
        (user.submitStatsGlobal?.acSubmissionNum || []).map(x => [x.difficulty, x.count])
      )
      const beats = Object.fromEntries(
        (user.problemsSolvedBeatsStats || []).map(x => [x.difficulty, x.percentage])
      )
      const diffs = {}
      for (const d of ["Easy", "Medium", "Hard"]) {
        diffs[d] = {
          solved: acNums[d] || 0,
          total: totals[d] || 0,
          beatsPercent: beats[d] ?? null,
        }
      }
      const solvedTotal = (acNums.All || 0) || Object.values(diffs).reduce((s, d) => s + d.solved, 0)
      const totalAll    = (totals.All || 0) || Object.values(diffs).reduce((s, d) => s + d.total, 0)
      setLc({
        username: user.username,
        url: `https://leetcode.com/u/${user.username}`,
        ranking: user.profile?.ranking ?? null,
        solved: solvedTotal,
        total: totalAll,
        difficulties: diffs,
      })
    } catch (err) {
      console.error("LeetCode verify failed:", err)
      setVerifyErr("LeetCode blocked the request. Save the handle anyway — stats will sync from mobile.")
    } finally { setVerifying(false) }
  }

  const verifyAction =
    type === "github"     ? verifyGithub :
    type === "leetcode"   ? verifyLeetcode :
    type === "codeforces" ? verifyCodeforces : null

  const currentProfile = { github: gh, leetcode: lc, codeforces: cf, medium: md }[type]
  const panel =
    type === "github"     ? <GitHubPanel profile={gh} /> :
    type === "leetcode"   ? <LeetCodePanel profile={lc} /> :
    type === "codeforces" ? <CodeforcesPanel profile={cf} /> :
    type === "medium"     ? <MediumPanel profile={md} /> : null

  const save = async () => {
    if (!userId) return
    setSaving(true)
    try {
      const payload = { [`socialLinks.${type}`]: normalized || "" }
      const profKey = PROFILE_KEY_BY_TYPE[type]
      if (profKey) {
        payload[`socialLinks.${profKey}`] = normalized ? (currentProfile || null) : deleteField()
      }
      await updateDoc(fsDoc(db, "users", userId), payload)
      toast.success(normalized ? "link saved" : "link removed")
      onClose?.()
    } catch (err) {
      console.error("Save link failed:", err)
      toast.error("couldn't save. try again.")
    } finally { setSaving(false) }
  }

  const remove = async () => {
    if (!userId) return
    setSaving(true)
    try {
      const payload = { [`socialLinks.${type}`]: "" }
      const profKey = PROFILE_KEY_BY_TYPE[type]
      if (profKey) payload[`socialLinks.${profKey}`] = deleteField()
      await updateDoc(fsDoc(db, "users", userId), payload)
      toast.success("link removed")
      onClose?.()
    } catch (err) {
      console.error("Remove link failed:", err)
      toast.error("couldn't remove. try again.")
    } finally { setSaving(false) }
  }

  const invalid = raw.trim() && !normalized
  const showPanel = Boolean(currentProfile) || type === "medium"

  return (
    <Modal title={`${current ? "Edit" : "Add"} ${t.label}`} onClose={onClose} maxWidth={620}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: P.inkDim,
            letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6,
          }}>
            {t.label} {type === "portfolio" ? "URL" : "handle"}
          </div>
          <div style={{
            display: "flex", alignItems: "stretch",
            borderRadius: 10, overflow: "hidden",
            background: P.pillBg,
            outline: invalid ? `1.5px solid ${P.danger}` : "none",
          }}>
            {type !== "portfolio" && (
              <span style={{
                padding: "10px 0 10px 13px",
                color: P.inkDim, fontSize: 13.5, fontFamily: FF,
                background: "transparent",
                whiteSpace: "nowrap",
              }}>
                {t.prefix}
              </span>
            )}
            <input
              value={raw} onChange={e => setRaw(e.target.value)}
              placeholder={type === "portfolio" ? "https://yourwebsite.com" : "username"}
              style={{
                flex: 1, border: "none", outline: "none",
                padding: type === "portfolio" ? "10px 13px" : "10px 13px 10px 0",
                fontSize: 13.5, color: P.ink,
                background: "transparent", fontFamily: FF,
              }}
            />
          </div>
          {invalid && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: P.danger }}>
              Doesn't look like a valid {t.label} link.
            </div>
          )}
          {normalized && !invalid && (
            <div style={{
              marginTop: 6, fontSize: 11.5, color: P.inkMuted,
              display: "flex", alignItems: "center", gap: 4,
            }}>
              <Check size={12} color={P.success} /> {normalized}
            </div>
          )}
        </div>

        {verifyAction && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <GhostButton onClick={verifyAction}
                         icon={<Loader2 size={12}
                                        className={verifying ? "pf-spinner" : ""} />}
                         disabled={!normalized || verifying}>
              {verifying ? "Checking…" : (currentProfile ? "Re-check" : "Fetch profile")}
            </GhostButton>
            {verifyErr && (
              <span style={{ fontSize: 11.5, color: P.danger, flex: 1, minWidth: 0 }}>
                {verifyErr}
              </span>
            )}
          </div>
        )}

        {showPanel && panel}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 6 }}>
          {current ? (
            <GhostButton onClick={remove} danger icon={<Trash2 size={12} />}>Remove</GhostButton>
          ) : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            <GhostButton onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton onClick={save} disabled={saving || invalid}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ── Logo avatar with API fallback ────────────────────────────────────────
function LogoAvatar({ name, logoUrl, size = 42 }) {
  const [failed, setFailed] = useState(false)
  const [failedApi, setFailedApi] = useState(false)
  const url = !failed && logoUrl ? logoUrl : (!failedApi && name ? companyLogoUrl(name) : "")
  const radius = Math.round(size * 0.26)
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: P.pillBg,
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden",
    }}>
      {url ? (
        <img src={url} alt="" referrerPolicy="no-referrer"
             style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" }}
             onError={() => { if (!failed) setFailed(true); else setFailedApi(true) }} />
      ) : (
        <span style={{
          fontSize: Math.round(size * 0.32), fontWeight: 700, color: P.inkMuted,
          letterSpacing: "-0.03em",
        }}>
          {initialsOf(name)}
        </span>
      )}
    </div>
  )
}

// ── Resume-derived sections (work, edu, achievements, projects, resume) ──
function WorkExperienceSection({ work, years }) {
  return (
    <SectionCard
      id="work-experience"
      eyebrow="Experience"
      title={years > 0 ? `${years}+ years of work experience` : "Work experience"}
      subtitle={work.length > 0 ? "Pulled from your latest resume." : undefined}
      icon={<Briefcase size={16} />}
      count={work.length}
    >
      {work.length === 0 ? (
        <EmptyHint
          icon={<Briefcase size={18} color={P.inkFaint} />}
          text="We couldn't pull any job history from your resume."
          hint="Reupload a resume if your experience is missing."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {work.map((w, i) => (
            <TimelineRow
              key={i}
              isLast={i === work.length - 1}
              logo={<LogoAvatar name={w.company} logoUrl={w.logoUrl} size={40} />}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: P.ink, letterSpacing: "-0.01em" }}>
                    {w.role}
                  </div>
                  <div style={{ fontSize: 13, color: P.inkMuted, marginTop: 2, fontWeight: 500 }}>
                    {w.company}{w.location ? <span style={{ color: P.inkFaint }}> · {w.location}</span> : null}
                  </div>
                </div>
                {w.isCurrentJob && <Pill color={P.success} subtle>Present</Pill>}
              </div>
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6,
                fontSize: 11.5, color: P.inkDim, fontWeight: 500,
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Clock size={11} /> {fmt(w.startMonth, w.startYear)} — {w.isCurrentJob ? "Now" : fmt(w.endMonth, w.endYear)}
                </span>
                {durationBetween(w) && (
                  <span>· {durationBetween(w)}</span>
                )}
              </div>
              {w.description && (
                <div style={{
                  marginTop: 8, fontSize: 13, color: P.ink2, lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}>
                  {w.description}
                </div>
              )}
            </TimelineRow>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

function monthIndex(month, year) {
  const y = parseInt(year, 10)
  if (!Number.isFinite(y)) return null
  const map = {
    jan:1,january:1, feb:2,february:2, mar:3,march:3, apr:4,april:4, may:5,
    jun:6,june:6, jul:7,july:7, aug:8,august:8,
    sep:9,sept:9,september:9, oct:10,october:10, nov:11,november:11, dec:12,december:12,
  }
  const key = String(month).trim().toLowerCase()
  const m = map[key] || parseInt(key, 10)
  if (!Number.isFinite(m) || m < 1 || m > 12) return null
  return y * 12 + m
}

function durationBetween(w) {
  const start = monthIndex(w.startMonth, w.startYear)
  const end = w.isCurrentJob
    ? (new Date().getFullYear() * 12 + (new Date().getMonth() + 1))
    : monthIndex(w.endMonth, w.endYear)
  if (!start || !end || end < start) return ""
  const months = Math.max(1, end - start)
  if (months < 12) return `${months}m`
  const years = Math.floor(months / 12)
  const rem = months % 12
  return rem === 0 ? `${years}y` : `${years}y ${rem}m`
}

function Pill({ children, color = P.ink, subtle }) {
  return (
    <span style={{
      padding: "3px 9px", borderRadius: 999,
      background: subtle ? `${color}18` : color,
      color: subtle ? color : "#fff",
      fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
      display: "inline-flex", alignItems: "center", gap: 4,
      flexShrink: 0,
    }}>
      {subtle && (
        <span aria-hidden="true" style={{
          width: 6, height: 6, borderRadius: "50%", background: color,
        }} />
      )}
      {children}
    </span>
  )
}

function TimelineRow({ logo, isLast, children }) {
  return (
    <div style={{
      display: "flex", gap: 14, alignItems: "stretch",
      paddingBottom: isLast ? 0 : 20,
    }}>
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0,
      }}>
        {logo}
        {!isLast && (
          <div style={{
            flex: 1, width: 1.5, marginTop: 6, marginBottom: -14,
            background: `linear-gradient(to bottom, ${P.border}, ${P.divider})`,
          }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        {children}
      </div>
    </div>
  )
}

function EducationSection({ education }) {
  return (
    <SectionCard
      id="education"
      eyebrow="Schooling"
      title="Education"
      icon={<GraduationCap size={16} />}
      count={education.length}
    >
      {education.length === 0 ? (
        <EmptyHint
          icon={<GraduationCap size={18} color={P.inkFaint} />}
          text="No education details in your resume."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {education.map((e, i) => {
            const title = e.degree && e.field ? `${e.degree} in ${e.field}` : (e.degree || e.field || "")
            const dates = [fmt(e.startMonth, e.startYear), fmt(e.endMonth, e.endYear)]
              .filter(Boolean).join(" – ")
            return (
              <TimelineRow key={i} isLast={i === education.length - 1}
                logo={<LogoAvatar name={e.institute} logoUrl={e.logoUrl} size={40} />}>
                <div style={{ fontSize: 14.5, fontWeight: 800, color: P.ink, letterSpacing: "-0.01em" }}>
                  {e.institute}
                </div>
                {title && (
                  <div style={{ fontSize: 13, color: P.inkMuted, marginTop: 2 }}>
                    {title}
                  </div>
                )}
                {dates && (
                  <div style={{
                    marginTop: 6, fontSize: 11.5, color: P.inkDim, fontWeight: 500,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <Clock size={11} /> {dates}
                  </div>
                )}
              </TimelineRow>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

function AchievementsSection({ achievements }) {
  return (
    <SectionCard
      id="achievements"
      eyebrow="Highlights"
      title="Achievements"
      icon={<Trophy size={16} />}
      count={achievements.length}
    >
      {achievements.length === 0 ? (
        <EmptyHint icon={<Trophy size={18} color={P.inkFaint} />} text="No achievements captured yet." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {achievements.map((a, i) => (
            <div key={i} style={{
              padding: "14px 16px", borderRadius: 14, background: P.pillBg,
            }}>
              <div style={{
                fontSize: 13.5, fontWeight: 700, color: P.ink,
                letterSpacing: "-0.01em",
              }}>
                {a.title}
              </div>
              {a.detail && (
                <div style={{
                  marginTop: 6, fontSize: 12.5, color: P.inkMuted, lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                }}>
                  {a.detail}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

function ProjectsSection({ projects }) {
  return (
    <SectionCard
      id="projects"
      eyebrow="Builds"
      title="Projects"
      icon={<FolderGit2 size={16} />}
      count={projects.length}
    >
      {projects.length === 0 ? (
        <EmptyHint text="No projects in your resume yet." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {projects.map((p, i) => (
            <div key={i} style={{
              padding: "14px 16px", borderRadius: 14, background: P.pillBg,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  fontSize: 13.5, fontWeight: 700, color: P.ink, flex: 1,
                  letterSpacing: "-0.01em",
                }}>
                  {p.title}
                </div>
                {p.url && (
                  <a href={p.url} target="_blank" rel="noreferrer" aria-label="Open project"
                     style={{
                       width: 28, height: 28, borderRadius: 8,
                       background: "#fff", color: P.inkMuted, flexShrink: 0,
                       display: "inline-flex", alignItems: "center", justifyContent: "center",
                     }}>
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>
              {p.description && (
                <div style={{
                  marginTop: 6, fontSize: 12.5, color: P.inkMuted, lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                }}>
                  {p.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

function ResumeSection({ resume, previewMode, resumes, activeId, onSetDefault, onUpload, onDelete }) {
  return (
    <SectionCard
      id="resume"
      eyebrow="File"
      title="Resume"
      subtitle={activeId ? "Your default resume powers all role matches." : "Upload a resume to unlock role matches."}
      icon={<FileText size={16} />}
      action={!previewMode && (
        <GhostButton icon={<Plus size={13} />} onClick={onUpload}>Upload new</GhostButton>
      )}
    >
      {resumes.length === 0 ? (
        <EmptyHint text="Upload your resume to unlock role matches." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {resumes.map(r => {
            const active = r.id === activeId
            const label = r.fileName || r.name
            return (
              <div key={r.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderRadius: 12,
                background: P.pillBg,
              }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 9,
                  background: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: active ? P.ink : P.inkMuted, flexShrink: 0,
                }}>
                  <FileText size={15} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: P.ink,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    display: "flex", alignItems: "center", gap: 6,
                    letterSpacing: "-0.01em",
                  }}>
                    <span style={{
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{label.replace(/\.pdf$/i, "")}</span>
                    {active && (
                      <span style={{
                        padding: "1px 7px", borderRadius: 999, background: P.ink,
                        color: "#fff", fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.04em", flexShrink: 0,
                      }}>
                        DEFAULT
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: P.inkDim, marginTop: 2 }}>
                    {active ? "Currently powering role matches" : "Not matching right now"}
                  </div>
                </div>
                {r.publicUrl && (
                  <a href={r.publicUrl} target="_blank" rel="noreferrer" aria-label="View resume"
                     style={{
                       width: 28, height: 28, borderRadius: 8,
                       background: "#fff", color: P.inkMuted, flexShrink: 0,
                       display: "inline-flex", alignItems: "center", justifyContent: "center",
                     }}>
                    <ExternalLink size={13} />
                  </a>
                )}
                {!previewMode && !active && (
                  <GhostButton onClick={() => onSetDefault(r.id)}>Make default</GhostButton>
                )}
                {!previewMode && (
                  <GhostButton
                    danger
                    icon={<Trash2 size={12} />}
                    disabled={resumes.length <= 1}
                    onClick={() => onDelete(r)}
                  >
                    Delete
                  </GhostButton>
                )}
              </div>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

function EmptyHint({ icon, text, hint }) {
  return (
    <div style={{
      padding: "18px 20px", borderRadius: 14,
      background: P.pillBg,
      color: P.inkMuted, fontSize: 13, lineHeight: 1.5,
      display: "flex", gap: 14, alignItems: hint ? "flex-start" : "center",
    }}>
      {icon && (
        <span style={{
          width: 36, height: 36, borderRadius: 11, flexShrink: 0,
          background: "#fff", display: "inline-flex",
          alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: P.ink2, fontWeight: 600, letterSpacing: "-0.005em" }}>
          {text}
        </div>
        {hint && (
          <div style={{ marginTop: 3, fontSize: 12, color: P.inkDim, lineHeight: 1.5 }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Boss / public profile view ───────────────────────────────────────────
function BossSection({ label, children }) {
  return (
    <section>
      <div style={{
        fontSize: 10, fontWeight: 700, color: P.inkFaint,
        letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 20,
      }}>
        {label}
      </div>
      {children}
    </section>
  )
}

function BossPromptCard({ prompt }) {
  return (
    <figure style={{
      margin: 0, padding: "18px 20px",
      borderRadius: 14, background: P.surfaceAlt,
      display: "flex", gap: 12, alignItems: "flex-start",
    }}>
      <Quote
        size={18}
        style={{ color: P.inkFaint, flexShrink: 0, marginTop: 3 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <figcaption style={{
          fontSize: 12, fontWeight: 600, color: P.inkMuted,
          marginBottom: 8, letterSpacing: "-0.005em",
        }}>
          {prompt.prompt}
        </figcaption>
        <blockquote style={{
          margin: 0, fontSize: 14.5, color: P.ink, lineHeight: 1.6,
          whiteSpace: "pre-wrap", fontWeight: 500, letterSpacing: "-0.01em",
        }}>
          {prompt.answer}
        </blockquote>
      </div>
    </figure>
  )
}

function BossResumeCanvas({ url }) {
  const containerRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const update = () => setWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const fileProp = useMemo(
    () => ({ url: url + (url.includes("?") ? "&" : "?") + "v=1" }),
    [url]
  )
  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Document
        file={fileProp}
        onLoadSuccess={({ numPages: n }) => { setErr(null); setNumPages(n) }}
        onLoadError={e => { console.error("[BossResumeCanvas] load error", e); setErr(e) }}
        loading={
          <div style={{ padding: 24, textAlign: "center", color: P.inkDim, fontSize: 13 }}>
            Loading résumé…
          </div>
        }
        error={
          <div style={{ padding: 24, textAlign: "center", color: P.danger, fontSize: 13 }}>
            Could not load résumé{err?.message ? `: ${err.message}` : ""}
          </div>
        }
        noData=""
      >
        {width > 0 && Array.from({ length: numPages }, (_, i) => (
          <div key={i} style={{
            marginBottom: i < numPages - 1 ? 14 : 0,
            borderRadius: 12,
            boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 24px rgba(15,23,42,0.06)",
            overflow: "hidden", background: "#fff",
          }}>
            <Page pageNumber={i + 1} width={width}
                  renderAnnotationLayer={false} renderTextLayer={false} />
          </div>
        ))}
      </Document>
    </div>
  )
}

function BossListRow({ title, description, url, isFirst }) {
  return (
    <div style={{
      padding: "18px 0",
      borderTop: isFirst ? "none" : `1px solid ${P.divider}`,
      display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15, fontWeight: 700, color: P.ink,
          letterSpacing: "-0.015em", lineHeight: 1.3,
        }}>
          {title}
        </div>
        {description && (
          <div style={{
            marginTop: 6, fontSize: 13.5, color: P.inkMuted, lineHeight: 1.55,
            whiteSpace: "pre-wrap",
          }}>
            {description}
          </div>
        )}
      </div>
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
           aria-label="Open link"
           style={{
             width: 32, height: 32, borderRadius: 10, flexShrink: 0,
             background: P.pillBg, color: P.inkMuted,
             display: "inline-flex", alignItems: "center", justifyContent: "center",
             transition: "background 0.15s, color 0.15s",
             textDecoration: "none",
           }}
           onMouseEnter={e => { e.currentTarget.style.background = "#E8E8EB"; e.currentTarget.style.color = P.ink }}
           onMouseLeave={e => { e.currentTarget.style.background = P.pillBg; e.currentTarget.style.color = P.inkMuted }}
        >
          <ExternalLink size={13} />
        </a>
      )}
    </div>
  )
}

function CollapsibleDescription({ text }) {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px 4px 12px", borderRadius: 999,
          background: P.pillBg, border: "none", color: P.inkMuted,
          fontSize: 11.5, fontWeight: 600, fontFamily: FF, cursor: "pointer",
          letterSpacing: "-0.005em", transition: "background 0.12s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#EBEBEE"}
        onMouseLeave={e => e.currentTarget.style.background = P.pillBg}
      >
        {open ? "Hide details" : "Show details"}
        <ChevronDown size={13} style={{
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }} />
      </button>
      <div style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{
            marginTop: 10, fontSize: 13, color: P.ink2,
            lineHeight: 1.6, whiteSpace: "pre-wrap",
          }}>
            {text}
          </div>
        </div>
      </div>
    </div>
  )
}

function BossRightPanel({
  userData, workHistory, education, achievements, projects,
  activeResume, prompts, onExit,
}) {
  const yoe = toInt(userData?.experience)
  const links = userData?.socialLinks || {}
  const connectedSocials = SOCIAL_TYPES.filter(t => links[t.k])
  const SERVICES_WITH_PANEL = new Set(["github", "leetcode", "codeforces", "medium"])

  const resumeUrl = activeResume?.publicUrl || ""
  const promptList = (prompts || []).filter(p => p?.prompt && p?.answer)

  const name = userData?.name || "Anonymous"
  const email = userData?.email || ""
  const phone = userData?.phone || userData?.additionalInfo?.phone || ""
  const avatar = userData?.profileUrl || ""
  const roleTitle = userData?.currentJobTitle || userData?.currJobTitle || ""
  const currentJob = workHistory.find(w => w.isCurrentJob) || workHistory[0]
  const currentCompany = userData?.currentCompany || currentJob?.company || ""
  const subtitle = [roleTitle, currentCompany].filter(Boolean).join(" at ")
  const prefLocs = (userData?.filters?.locations || []).map(prettyCity).filter(Boolean)
  const locationStr = userData?.location || prefLocs[0] || ""

  return (
    <div className="pf-boss">
      <style>{`
        @keyframes pf-boss-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pf-boss > article { animation: pf-boss-in 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
      `}</style>

      {/* Public profile sheet — content on left, résumé on right */}
      <article style={{
        fontFamily: FF,
      }}>
        <div className={resumeUrl ? "pf-boss-split" : ""} style={
          resumeUrl ? {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, 0.9fr)",
            gap: 56, alignItems: "flex-start",
          } : {}
        }>
        {/* Left column: banner + hero + content, stacked */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
        {/* Preview banner — full width of left column */}
        <div style={{
          background: "#fff", color: P.ink,
          padding: "8px 10px 8px 14px", borderRadius: 999,
          boxShadow: "0 1px 2px rgba(15,23,42,0.05), 0 0 0 1px rgba(15,23,42,0.04)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12,
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 9,
            fontSize: 12.5, fontWeight: 600, color: P.ink2,
            letterSpacing: "-0.005em", minWidth: 0,
          }}>
            <span style={{
              width: 20, height: 20, borderRadius: 999, flexShrink: 0,
              background: P.pillBg, color: P.ink,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
              <Eye size={11} />
            </span>
            <span style={{
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              Previewing — this is what a boss sees
            </span>
          </div>
          <button onClick={onExit} style={{
            padding: "5px 11px 5px 9px", borderRadius: 999, border: "none",
            background: P.ink, color: "#fff", flexShrink: 0,
            cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: FF,
            display: "inline-flex", alignItems: "center", gap: 5,
            transition: "background 0.12s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "#000"}
            onMouseLeave={e => e.currentTarget.style.background = P.ink}
          >
            <X size={12} /> Exit
          </button>
        </div>
        {/* Hero header — avatar + identity + contact */}
        <header style={{
          display: "flex", alignItems: "center", gap: 20,
          padding: "28px 32px",
          background: "#fff", borderRadius: 20,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        }}>
          <div style={{
            width: 92, height: 92, borderRadius: "50%", flexShrink: 0,
            overflow: "hidden", background: P.pillBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 1px 2px rgba(15,23,42,0.05), 0 0 0 1px rgba(15,23,42,0.04)",
          }}>
            {avatar ? (
              <img src={avatar} alt=""
                   style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <span style={{
                fontSize: 34, fontWeight: 700, color: P.ink,
                letterSpacing: "-0.03em",
              }}>
                {initialsOf(name)}
              </span>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              margin: 0, fontSize: 26, fontWeight: 800, color: P.ink,
              letterSpacing: "-0.03em", lineHeight: 1.15,
            }}>
              {name}
            </h1>
            {subtitle && (
              <div style={{
                marginTop: 4, fontSize: 14, color: P.inkMuted, fontWeight: 500,
                letterSpacing: "-0.005em",
              }}>
                {subtitle}
              </div>
            )}
            <div style={{
              marginTop: 10, display: "flex", flexWrap: "wrap",
              rowGap: 6, columnGap: 16, fontSize: 12.5, color: P.inkMuted,
            }}>
              {email && (
                <a href={`mailto:${email}`} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  color: P.inkMuted, textDecoration: "none",
                }}>
                  <Mail size={13} color={P.inkDim} /> {email}
                </a>
              )}
              {phone && (
                <a href={`tel:${phone}`} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  color: P.inkMuted, textDecoration: "none",
                }}>
                  <Phone size={13} color={P.inkDim} /> {phone}
                </a>
              )}
              {locationStr && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}>
                  <MapPin size={13} color={P.inkDim} /> {locationStr}
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Content stack */}
        <div style={{
          display: "flex", flexDirection: "column", gap: 44, minWidth: 0,
          padding: "32px", background: "#fff", borderRadius: 20,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        }}>
          {workHistory.length > 0 && (
            <BossSection
              label={yoe > 0 ? `${yoe}+ years of work experience` : "Work experience"}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                {workHistory.map((w, i) => (
                  <TimelineRow key={i} isLast={i === workHistory.length - 1}
                    logo={<LogoAvatar name={w.company} logoUrl={w.logoUrl} size={44} />}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 15, fontWeight: 800, color: P.ink,
                          letterSpacing: "-0.015em",
                        }}>
                          {w.role}
                        </div>
                        <div style={{
                          fontSize: 13, color: P.inkMuted, marginTop: 2, fontWeight: 500,
                        }}>
                          {w.company}
                          {w.location && <span style={{ color: P.inkFaint }}> · {w.location}</span>}
                        </div>
                      </div>
                      {w.isCurrentJob && <Pill color={P.success} subtle>Present</Pill>}
                    </div>
                    <div style={{
                      display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6,
                      fontSize: 11.5, color: P.inkDim, fontWeight: 500,
                    }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Clock size={11} /> {fmt(w.startMonth, w.startYear)} — {w.isCurrentJob ? "Now" : fmt(w.endMonth, w.endYear)}
                      </span>
                      {durationBetween(w) && <span>· {durationBetween(w)}</span>}
                    </div>
                    {w.description && <CollapsibleDescription text={w.description} />}
                  </TimelineRow>
                ))}
              </div>
            </BossSection>
          )}

          {promptList[0] && <BossPromptCard prompt={promptList[0]} />}

          {education.length > 0 && (
            <BossSection label="Education">
              <div style={{ display: "flex", flexDirection: "column" }}>
                {education.map((e, i) => {
                  const title = e.degree && e.field
                    ? `${e.degree} in ${e.field}`
                    : (e.degree || e.field || "")
                  const dates = [fmt(e.startMonth, e.startYear), fmt(e.endMonth, e.endYear)]
                    .filter(Boolean).join(" – ")
                  return (
                    <TimelineRow key={i} isLast={i === education.length - 1}
                      logo={<LogoAvatar name={e.institute} logoUrl={e.logoUrl} size={44} />}>
                      <div style={{
                        fontSize: 15, fontWeight: 800, color: P.ink,
                        letterSpacing: "-0.015em",
                      }}>
                        {e.institute}
                      </div>
                      {title && (
                        <div style={{ fontSize: 13, color: P.inkMuted, marginTop: 2 }}>
                          {title}
                        </div>
                      )}
                      {dates && (
                        <div style={{
                          marginTop: 6, fontSize: 11.5, color: P.inkDim, fontWeight: 500,
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                          <Clock size={11} /> {dates}
                        </div>
                      )}
                    </TimelineRow>
                  )
                })}
              </div>
            </BossSection>
          )}

          {promptList[1] && <BossPromptCard prompt={promptList[1]} />}

          {connectedSocials.length > 0 && (
            <BossSection label="Social presence">
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
              }}>
                {connectedSocials.map(t => {
                  const url = links[t.k]
                  if (SERVICES_WITH_PANEL.has(t.k)) {
                    const profileKey = PROFILE_KEY_BY_TYPE[t.k]
                    const profile = profileKey ? links[profileKey] : null
                    const commonProfile = profile || { url }
                    if (t.k === "github") return (
                      <GitHubPanel key={t.k}
                        profile={profile || { login: stripSocialPathPrefix(t.k, (() => { try { return new URL(url).pathname } catch { return "" } })()), url }} />
                    )
                    if (t.k === "leetcode") return (
                      <LeetCodePanel key={t.k}
                        profile={profile || { username: stripSocialPathPrefix(t.k, (() => { try { return new URL(url).pathname } catch { return "" } })()), url }} />
                    )
                    if (t.k === "codeforces") return (
                      <CodeforcesPanel key={t.k}
                        profile={profile || { handle: stripSocialPathPrefix(t.k, (() => { try { return new URL(url).pathname } catch { return "" } })()), url }} />
                    )
                    if (t.k === "medium") return (
                      <div key={t.k} style={{ gridColumn: "1 / -1" }}>
                        <MediumPanel
                          profile={profile || { username: stripSocialPathPrefix(t.k, (() => { try { return new URL(url).pathname } catch { return "" } })()), url }} />
                      </div>
                    )
                  }
                  const handle = (() => {
                    try {
                      const u = new URL(url)
                      const first = stripSocialPathPrefix(t.k, u.pathname).split("/").filter(Boolean)[0] || ""
                      return first ? `@${first}` : u.host.replace(/^www\./, "")
                    } catch { return url }
                  })()
                  return (
                    <a href={url} target="_blank" rel="noreferrer" key={t.k}
                       style={{
                         textDecoration: "none",
                         padding: "14px 16px", borderRadius: 14,
                         background: P.pillBg, display: "block",
                         gridColumn: "1 / -1",
                         transition: "background 0.15s",
                       }}
                       onMouseEnter={e => e.currentTarget.style.background = "#EBEBEE"}
                       onMouseLeave={e => e.currentTarget.style.background = P.pillBg}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                        <span style={{
                          width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                          background: "#fff", color: t.color,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <SocialIcon type={t.k} size={20} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 700, color: P.ink,
                            letterSpacing: "-0.01em",
                          }}>
                            {t.label}
                          </div>
                          <div style={{
                            fontSize: 11.5, color: P.inkMuted, marginTop: 2,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                            {handle}
                          </div>
                        </div>
                        <ExternalLink size={13} color={P.inkDim} />
                      </div>
                    </a>
                  )
                })}
              </div>
            </BossSection>
          )}

          {projects.length > 0 && (
            <BossSection label="Projects">
              <div>
                {projects.map((p, i) => (
                  <BossListRow key={i} title={p.title} description={p.description}
                               url={p.url} isFirst={i === 0} />
                ))}
              </div>
            </BossSection>
          )}

          {promptList[2] && <BossPromptCard prompt={promptList[2]} />}

          {achievements.length > 0 && (
            <BossSection label="Achievements">
              <div>
                {achievements.map((a, i) => (
                  <BossListRow key={i} title={a.title} description={a.detail}
                               isFirst={i === 0} />
                ))}
              </div>
            </BossSection>
          )}

        </div>
        </div>

        {resumeUrl && (
          <aside style={{
            position: "sticky", top: 16,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            padding: "28px", background: "#fff", borderRadius: 20,
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: P.inkFaint,
                letterSpacing: "0.22em", textTransform: "uppercase",
              }}>
                Résumé
              </div>
              <a href={resumeUrl} target="_blank" rel="noreferrer"
                 aria-label="Open résumé in new tab"
                 style={{
                   width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                   background: P.pillBg, color: P.inkMuted,
                   display: "inline-flex", alignItems: "center", justifyContent: "center",
                   textDecoration: "none",
                 }}>
                <ExternalLink size={12} />
              </a>
            </div>
            <BossResumeCanvas url={resumeUrl} />
          </aside>
        )}
        </div>
      </article>
    </div>
  )
}

// ── Main ProfileTab ──────────────────────────────────────────────────────
export default function ProfileTab({ userId, userData }) {
  const [previewMode, setPreviewMode] = useState(false)
  const [prefsOpen, setPrefsOpen] = useState(false)
  const [promptIdx, setPromptIdx] = useState(null) // null = closed, number = index (new if === prompts.length)
  const [linkType, setLinkType] = useState(null)   // null | socialType
  const [uploadOpen, setUploadOpen] = useState(false)

  // Live resume list (defaults from Firestore sub-collection)
  const [resumes, setResumes] = useState([])
  const activeResumeId = userData?.filters?.resumeId || userData?.resume?.name || ""
  useEffect(() => {
    if (!userId) return
    const unsub = onSnapshot(collection(db, "users", userId, "resumes"), snap => {
      const list = snap.docs.map(d => ({ name: d.id, ...d.data(), id: d.id }))
      list.sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0))
      setResumes(list)
    })
    return () => unsub()
  }, [userId])

  const activeResume = useMemo(
    () => resumes.find(r => r.id === activeResumeId) || resumes[0] || null,
    [resumes, activeResumeId]
  )

  // Lift resume sections from the active resume doc
  const workHistory = useMemo(() => {
    const list = activeResume?.work_history || []
    return list.map(w => ({
      company: w.companyName || "", role: w.role || "",
      startMonth: w.startMonth || "", startYear: w.startYear || "",
      endMonth: w.endMonth || "", endYear: w.endYear || "",
      isCurrentJob: !!w.isCurrentJob, description: w.description || "",
      location: w.location || w.city || w.jobLocation || "",
      logoUrl: w.logoUrl || "",
    }))
  }, [activeResume])

  const education = useMemo(() => {
    return (activeResume?.education || []).map(e => ({
      degree: e.degree || "", field: e.field || "",
      institute: e.institute || "",
      startMonth: e.startMonth || "", startYear: e.startYear || "",
      endMonth: e.endMonth || "", endYear: e.endYear || "",
      logoUrl: e.logoUrl || "",
    }))
  }, [activeResume])

  const achievements = useMemo(() => {
    return (activeResume?.achievements || []).map(a => ({
      title: a.title || "", detail: a.achievement || a.detail || "",
    }))
  }, [activeResume])

  const projects = useMemo(() => {
    return (activeResume?.projects || []).map(p => ({
      title: p.title || "", description: p.description || "", url: p.url || "",
    }))
  }, [activeResume])

  const prompts = userData?.prompts || []

  // ── Prompt operations ────────────────────────────────────────────────
  const savePrompt = async (idx, value) => {
    if (!userId) return
    const existing = (userData?.prompts || []).filter(p => p?.prompt && p?.answer)
    const next = [...existing]
    if (idx >= next.length) next.push(value)
    else next[idx] = value
    await updateDoc(fsDoc(db, "users", userId), { prompts: next })
  }
  const deletePrompt = async (idx) => {
    if (!userId) return
    if (!window.confirm("Delete this prompt?")) return
    const existing = (userData?.prompts || []).filter(p => p?.prompt && p?.answer)
    const next = existing.filter((_, i) => i !== idx)
    await updateDoc(fsDoc(db, "users", userId), { prompts: next })
  }

  // ── Resume operations ────────────────────────────────────────────────
  const setDefaultResume = async (resumeId) => {
    if (!userId) return
    try {
      const resumeDoc = await getDoc(fsDoc(db, "users", userId, "resumes", resumeId))
      const taxonomies = (resumeDoc.data()?.filters?.taxonomies) || []
      // Normalize legacy shape → [{taxonomyId, title}]
      const normalized = Array.isArray(taxonomies) && taxonomies.length && typeof taxonomies[0] === "object"
        ? taxonomies
        : taxonomies.map(id => ({ taxonomyId: id, title: "" }))
      await setDoc(fsDoc(db, "users", userId), {
        filters: { resumeId, taxonomies: normalized },
      }, { merge: true })
      toast.success("default resume updated")
    } catch (err) {
      console.error("Set default resume failed:", err)
      toast.error("couldn't update default resume.")
    }
  }

  const deleteResume = async (r) => {
    if (!userId || !r?.id) return
    if (resumes.length <= 1) {
      toast.error("upload another resume before deleting this one")
      return
    }
    if (!window.confirm(`Delete "${r.fileName || r.name}"?`)) return
    try {
      await deleteDoc(fsDoc(db, "users", userId, "resumes", r.id))
      const userPatch = {}
      if (userData?.filters?.resumeId === r.id) userPatch["filters.resumeId"] = deleteField()
      if (userData?.resume?.name === r.id)      userPatch.resume = deleteField()
      if (Object.keys(userPatch).length) {
        await updateDoc(fsDoc(db, "users", userId), userPatch)
      }
      toast.success("resume deleted")
    } catch (err) {
      console.error("Resume delete failed:", err)
      toast.error("couldn't delete resume.")
    }
  }

  // Render guards
  if (!userId) {
    return (
      <div style={{
        padding: 60, textAlign: "center", color: P.inkFaint, fontFamily: FF,
      }}>
        sign in to view your profile.
      </div>
    )
  }
  if (!userData) {
    return (
      <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
        <span className="pf-spinner" style={{
          width: 24, height: 24, borderRadius: "50%",
          border: `2px solid ${P.border}`, borderTopColor: P.ink,
        }} />
      </div>
    )
  }

  return (
    <div style={{ padding: "4px 0 32px", fontFamily: FF }}>
      <style>{`
        @keyframes pf-spin { to { transform: rotate(360deg); } }
        .pf-spinner { animation: pf-spin 0.75s linear infinite; display: inline-block; }
        .pf-grid {
          display: grid;
          grid-template-columns: 300px minmax(0, 1fr);
          gap: 20px;
          align-items: start;
        }
        .pf-grid--preview {
          grid-template-columns: minmax(0, 1fr);
        }
        .pf-social-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
          gap: 10px;
        }
        @media (max-width: 1180px) {
          .pf-boss-split { grid-template-columns: 1fr !important; }
          .pf-boss-split > aside {
            position: static !important;
            max-height: none !important;
            overflow-y: visible !important;
          }
        }
        @media (max-width: 980px) {
          .pf-grid { grid-template-columns: 1fr; }
          .pf-grid > aside { position: static !important; }
        }
      `}</style>

      <div className={previewMode ? "pf-grid pf-grid--preview" : "pf-grid"}>
        {!previewMode && (
          <HeroCard
            userData={userData}
            userId={userId}
            previewMode={previewMode}
            onTogglePreview={() => setPreviewMode(p => !p)}
          />
        )}
        <div>
          {previewMode ? (
            <BossRightPanel
              userData={userData}
              workHistory={workHistory}
              education={education}
              achievements={achievements}
              projects={projects}
              activeResume={activeResume}
              prompts={prompts}
              onExit={() => setPreviewMode(false)}
            />
          ) : (
            <>
              <JobPreferencesSection
                userData={userData}
                previewMode={previewMode}
                onEdit={() => setPrefsOpen(true)}
              />
              <PromptsSection
                userData={userData}
                previewMode={previewMode}
                onAdd={(len) => setPromptIdx(len)}
                onEdit={(i) => setPromptIdx(i)}
                onDelete={deletePrompt}
              />
              <SocialLinksSection
                userData={userData}
                previewMode={previewMode}
                onEdit={setLinkType}
              />
              <WorkExperienceSection work={workHistory} years={toInt(userData?.experience)} />
              <EducationSection education={education} />
              <AchievementsSection achievements={achievements} />
              <ProjectsSection projects={projects} />
              <ResumeSection
                resume={activeResume}
                previewMode={previewMode}
                resumes={resumes}
                activeId={activeResumeId}
                onSetDefault={setDefaultResume}
                onUpload={() => setUploadOpen(true)}
                onDelete={deleteResume}
              />
            </>
          )}
        </div>
      </div>

      {prefsOpen && (
        <JobPreferencesModal
          userData={userData}
          userId={userId}
          onClose={() => setPrefsOpen(false)}
        />
      )}
      {promptIdx !== null && (
        <PromptEditorModal
          initial={prompts.filter(p => p?.prompt && p?.answer)[promptIdx]}
          usedPrompts={prompts.filter(p => p?.prompt && p?.answer).map(p => p.prompt)}
          onSave={async (val) => { await savePrompt(promptIdx, val); setPromptIdx(null) }}
          onClose={() => setPromptIdx(null)}
        />
      )}
      {linkType && (
        <SocialLinkModal
          type={linkType}
          userData={userData}
          userId={userId}
          onClose={() => setLinkType(null)}
        />
      )}
      {uploadOpen && (
        <AddResumeModal
          userId={userId}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => setUploadOpen(false)}
        />
      )}
    </div>
  )
}
